'use client';

import { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Search, ChevronDown } from 'lucide-react';

type Brand = {
  name: string;
  slug: string;
  category: string;
};

export default function TemplateGrid({ brands, categories }: { brands: Brand[]; categories: string[] }) {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredBrands = brands.filter(brand => {
    const matchesCategory = selectedCategory === 'All' || brand.category === selectedCategory;
    const matchesSearch = brand.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <>
      {/* Search Bar */}
      <div className="flex justify-center mb-8">
        <div className="relative w-full max-w-[400px]">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search templates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full h-12 pl-12 pr-4 rounded-lg border-2 border-[#E0E0E0] focus:border-[#0068D6] focus:outline-none transition-colors"
            style={{ fontFamily: 'Roboto', fontSize: '16px' }}
          />
        </div>
      </div>

      {/* Category Dropdown - Mobile */}
      <div className="md:hidden flex justify-center mb-8">
        <div className="relative w-full max-w-[300px]">
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="w-full h-12 px-4 pr-10 rounded-lg border-2 border-[#0068D6] bg-white appearance-none cursor-pointer focus:outline-none"
            style={{ fontFamily: 'Roboto', fontSize: '16px', fontWeight: 500, color: '#1a1a2e' }}
          >
            {categories.map((category) => (
              <option key={category} value={category}>
                {category === 'All' ? 'All Templates' : category}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#0068D6] pointer-events-none" />
        </div>
      </div>

      {/* Category Tabs - Desktop */}
      <div className="hidden md:flex flex-wrap justify-center gap-2 mb-12">
        {categories.map((category) => (
          <button
            key={category}
            onClick={() => setSelectedCategory(category)}
            className="transition-colors"
            style={{
              fontFamily: 'Roboto', fontWeight: 500, fontSize: '14px',
              padding: '10px 20px', borderRadius: '6px',
              border: selectedCategory === category ? '2px solid #0068D6' : '2px solid #E0E0E0',
              backgroundColor: selectedCategory === category ? '#0068D6' : '#FFFFFF',
              color: selectedCategory === category ? '#FFFFFF' : '#1a1a2e',
              cursor: 'pointer',
            }}
          >
            {category === 'All' ? 'All Templates' : category}
          </button>
        ))}
      </div>

      {/* Template Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
        {filteredBrands.map((brand, index) => (
          <Link
            key={index}
            href={`/${brand.slug}-receipt`}
            className="group cursor-pointer"
            style={{ textDecoration: 'none' }}
          >
            <div
              className="p-4 overflow-hidden h-full bg-[#E7F5FF] group-hover:bg-[#0068D6] transition-colors duration-200"
              style={{
                border: '2px solid transparent',
                borderRadius: '8px', display: 'flex', flexDirection: 'column',
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
              }}
            >
              <div className="flex flex-col items-center flex-1" style={{ overflow: 'hidden', position: 'relative' }}>
                <div className="flex items-start justify-center flex-1" style={{ width: '100%', overflow: 'hidden', position: 'relative', maxHeight: '280px' }}>
                  <Image
                    src={`/receipts/${brand.slug}-receipt-template.png`}
                    alt={`${brand.name} receipt template`}
                    width={280}
                    height={600}
                    sizes="(max-width: 768px) 45vw, (max-width: 1024px) 30vw, 20vw"
                    priority={index < 4}
                    fetchPriority={index < 4 ? "high" : "low"}
                    loading={index < 4 ? "eager" : "lazy"}
                    style={{ maxWidth: '100%', height: 'auto', objectFit: 'contain', objectPosition: 'top center', borderRadius: '0' }}
                  />
                  <div
                    className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#E7F5FF] group-hover:from-[#0068D6] to-transparent transition-colors duration-200"
                    style={{ height: '30%', pointerEvents: 'none' }}
                  />
                </div>
                <h3
                  className="text-center font-medium mt-4 text-[#1a1a2e] group-hover:text-white transition-colors duration-200"
                  style={{ fontFamily: 'Roboto', fontSize: '16px' }}
                >
                  {brand.name} Receipt
                </h3>
                <span
                  className="text-center mt-1 text-[#666666] group-hover:text-white/80 transition-colors duration-200"
                  style={{ fontFamily: 'Roboto', fontSize: '12px' }}
                >
                  {brand.category}
                </span>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {filteredBrands.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500" style={{ fontFamily: 'Roboto' }}>
            No templates found in this category.
          </p>
        </div>
      )}
    </>
  );
}
